import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@/app/store';

const GUARD_TIMEOUT_MS = 3000;

/**
 * Shared timeout logic: returns true while userTenantsLoaded is false
 * for authenticated users, with a 3-second safety timeout.
 */
function useTenantsLoadingGuard(): { isLoading: boolean; timedOut: boolean } {
  const isAuthenticated = useSelector((s: RootState) => s.auth.isAuthenticated);
  const userTenantsLoaded = useSelector((s: RootState) => s.tenant.userTenantsLoaded);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (!userTenantsLoaded && isAuthenticated) {
      setTimedOut(false);
      const timer = setTimeout(() => setTimedOut(true), GUARD_TIMEOUT_MS);
      return () => clearTimeout(timer);
    }
  }, [userTenantsLoaded, isAuthenticated]);

  const isLoading = isAuthenticated && !userTenantsLoaded && !timedOut;
  return { isLoading, timedOut };
}

/**
 * For `/` and `/main` pages: returns true while tenant state is resolving
 * OR a force-home redirect is imminent (forceWorkspaceHome + activeTenant).
 * Safety: unlocks after 3 seconds.
 */
export function useForceHomeGuard(): boolean {
  const { isLoading } = useTenantsLoadingGuard();
  const forceWorkspaceHome = useSelector((s: RootState) => s.tenant.forceWorkspaceHome);
  const activeTenant = useSelector((s: RootState) => s.tenant.activeTenant);

  if (isLoading) return true;
  if (forceWorkspaceHome && activeTenant) return true;
  return false;
}

/**
 * For `/workspace` page: returns true only while tenant state is resolving
 * during an account switch. Does NOT block on forceWorkspaceHome (workspace IS home).
 * Safety: unlocks after 3 seconds.
 */
export function useAccountSwitchGuard(): boolean {
  const { isLoading } = useTenantsLoadingGuard();
  return isLoading;
}
